import { Building2, Star, Award, CheckCircle, AlertCircle, Calendar } from 'lucide-react';

interface ReportHeaderProps {
  organization: {
    name: string;
    address: string;
    registrationNumber: string;
    taxId: string;
    idnat?: string;
    creditScore?: number;
    esgScore?: string;
    financialRating?: string;
    logo?: string;
  };
  title: string;
  isAudited: boolean;
  generatedBy: string;
  currency: 'USD' | 'CDF';
  fiscalYear?: string;
  generatedDate?: string;
  period?: {
    startDate?: string;
    endDate?: string;
  };
}

export function ReportHeader({
  organization,
  title,
  isAudited,
  generatedBy,
  currency,
  fiscalYear,
  generatedDate,
  period
}: ReportHeaderProps) {
  // Formater la période d'affichage
  const displayPeriod = (() => {
    if (fiscalYear) return `Exercice ${fiscalYear}`;
    if (period?.startDate && period?.endDate) {
      const start = new Date(period.startDate).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
      const end = new Date(period.endDate).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
      return `${start} - ${end}`;
    }
    return `Exercice ${new Date().getFullYear()}`;
  })();

  // Formater la date de génération
  const displayGeneratedDate = (() => {
    if (generatedDate) {
      const date = new Date(generatedDate);
      return `${date.toLocaleDateString('fr-FR')} à ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return `${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  })();

  return (
    <div className="space-y-6 border-b pb-6 mb-6">      {/* En-tête de l'entreprise */}
      <div className="flex justify-between">
        <div className="space-y-1 flex items-start">          {organization.logo ? (
            <img 
              src={organization.logo} 
              alt={`${organization.name} Logo`} 
              className="h-10 w-auto mr-3 object-contain"
              style={{ maxWidth: '50px' }}
            />
          ) : (
            <Building2 className="h-10 w-10 text-primary mr-3" />
          )}
          <div>
            <h2 className="text-xl font-bold">{organization.name}</h2>
            <p className="text-sm text-gray-600">{organization.address}</p>
            <p className="text-sm text-gray-600">
              RCCM: {organization.registrationNumber || 'N/A'}
              {organization.taxId && ` - NIF: ${organization.taxId}`}
              {organization.idnat && ` - IDNAT: ${organization.idnat}`}
            </p>
          </div>
        </div>
        
        <div className="text-right space-y-1">
          <div className="flex items-center justify-end space-x-2">
            <Star className="h-4 w-4 text-primary" />
            <p className="text-sm text-gray-600">
              <span className="font-medium">Cote crédit:</span> {organization.creditScore != null ? `${organization.creditScore}/100` : 'N/A'}
            </p>
          </div>
          <div className="flex items-center justify-end space-x-2">
            <Award className="h-4 w-4 text-success" />
            <p className="text-sm text-gray-600">
              <span className="font-medium">Code ESG:</span> {organization.esgScore}
            </p>
          </div>
          <div className="flex items-center justify-end space-x-2">
            <Star className="h-4 w-4 text-warning" />
            <p className="text-sm text-gray-600">
              <span className="font-medium">Note financière:</span> {organization.financialRating}
            </p>
          </div>
        </div>
      </div>

      {/* Informations du document */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="text-sm text-gray-600">{displayPeriod}</p>
        </div>

        <div>
          <p className="text-sm text-gray-600">Devise</p>
          <p className="font-medium">{currency}</p>
        </div>

        <div>
          <p className="text-sm text-gray-600">Généré par</p>
          <p className="font-medium">{generatedBy}</p>
        </div>

        <div>
          <p className="text-sm text-gray-600">Statut</p>
          <div className="flex items-center space-x-1">
            {isAudited ? (
              <>
                <CheckCircle className="h-4 w-4 text-success" />
                <span className="text-success font-medium">Audité</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-4 w-4 text-warning" />
                <span className="text-warning font-medium">Non audité</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center text-xs text-gray-500">
        <Calendar className="h-3 w-3 mr-1" />
        Document généré le {displayGeneratedDate}
      </div>
    </div>
  );
}